import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { McpAuthFrame, McpIntro, McpLoading } from '../components/McpUi';
import { authFetch } from '../utils/api';

const API = process.env.REACT_APP_API_BASE_URL;
const actions = ['credential_create', 'credential_rotate', 'channel_connect', 'account_delete'];

export default function McpContinue() {
  const { t } = useTranslation();
  const [request] = useState(() => new URLSearchParams(window.location.search).get('request'));
  const [info, setInfo] = useState(null);
  const [secret, setSecret] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (window.self !== window.top || !/^[0-9a-f-]{36}$/.test(request || '')) { setError('mcp_continue_invalid'); return undefined; }
    const controller = new AbortController();
    authFetch(`${API}mcp-configuration/requests/${request}`, { signal: controller.signal })
      .then(async response => {
        if (!response?.ok) throw new Error();
        const data = await response.json();
        if (!actions.includes(data.action)) throw new Error();
        if (!controller.signal.aborted) setInfo(data);
      }).catch(() => { if (!controller.signal.aborted) setError('mcp_continue_invalid'); });
    return () => controller.abort();
  }, [request]);
  const isCredential = info?.action.startsWith('credential_');
  const complete = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const response = await authFetch(`${API}mcp-configuration/requests/${request}`, {
        method: 'POST', body: JSON.stringify(isCredential ? { secret } : {}),
      });
      setSecret('');
      if (!response?.ok) {
        const failure = response ? await response.json() : {};
        setError(failure?.detail?.code === 'WORKSPACE_REQUIRED' ? 'mcp_continue_workspace' : 'mcp_continue_invalid');
        return;
      }
      const result = await response.json();
      // The server emits only these two in-app destinations. Never navigate
      // to an arbitrary response/query URL, even on a compromised continuation.
      if (result.path && /^\/(?:deploy\/channels\?|my-account\?)/.test(result.path)) {
        window.location.assign(result.path); return;
      }
      setDone(true);
    } catch { setSecret(''); setError('mcp_continue_invalid'); }
    finally { setBusy(false); }
  };
  return <McpAuthFrame>
    <McpIntro title={t(done ? 'mcp_continue_done' : `mcp_continue_${info?.action || 'title'}`)} subtitle={t('mcp_continue_subtitle')} />
    {error && <Alert severity="error" sx={{ mt: 3 }}>{t(error)}</Alert>}
    {!info && !error && <McpLoading />}
    {done ? <Alert severity="success" sx={{ mt: 3 }}>{t('mcp_provider_result_return')}</Alert> : info && <Stack spacing={3} sx={{ mt: 3 }}>
      <Box><Typography variant="body2" color="text.secondary">{info.email}</Typography><Chip label={info.workspace} sx={{ mt: 1 }} /></Box>
      <Alert severity={info.action === 'account_delete' ? 'warning' : 'info'}>{t(isCredential ? 'mcp_continue_secret_hint' : info.action === 'account_delete' ? 'mcp_continue_delete_hint' : 'mcp_continue_channel_hint')}</Alert>
      {isCredential && <>
        <Typography fontWeight={600}>{info.details.name} · {info.details.credential_type}</Typography>
        <TextField label={t('mcp_continue_secret')} type="password" fullWidth value={secret} onChange={event => setSecret(event.target.value)} autoComplete="new-password" inputProps={{ maxLength: 16000 }} />
      </>}
      {info.details.channel && <Typography>{info.details.channel} · {info.agent_id}</Typography>}
      <Button variant="contained" size="large" disabled={busy || (isCredential && !secret.trim())} onClick={complete}>{t(isCredential ? 'mcp_continue_save' : 'mcp_continue_open')}</Button>
      <Typography variant="caption" color="text.secondary">{t('mcp_continue_expiry')}</Typography>
    </Stack>}
  </McpAuthFrame>;
}
